import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RequestPageBase } from '@bo-client/shared/base-components/request-page.base';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { ConfigService } from '@bo-client/config/config.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ToastrService } from 'ngx-toastr';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { AuthenticationService } from '@bo-client/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { GridRegistrationRequestService } from '@bo-client/activation-management/grid-registration/grid-registration-request.service';

import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import {
  GridRegistrationTypeConst,
  GridRegistrationTypeEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { getComponentForGridRegistrationType } from '@bo-client/activation-management/grid-registration/grid-registration.utils';
import { GridRegistrationFormDirective } from '@bo-client/activation-management/grid-registration/grid-registration-form.directive';
import { ConsumerModel } from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { combineLatestWith, filter, takeUntil, tap } from 'rxjs/operators';
import { AnswerWithMoreInfoModel } from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { uri } from '@bo-client/shared/constants/uri';
import { Subject } from 'rxjs';
import { NBPConfig } from '@bo-client/shared/config.model';
import { path } from '@bo-client/shared/constants/path';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { PageActionExecutorService } from '@bo-client/shared/request-support/page-action-executor.service';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-grid-registration-external',
  templateUrl: './grid-registration-external.component.html',
  styleUrls: ['./grid-registration-external.component.scss']
})
export class GridRegistrationExternalComponent
  extends RequestPageBase<GridRegistrationModel>
  implements OnInit, OnDestroy
{
  private compUnsubs$ = new Subject<void>();

  @ViewChild(GridRegistrationFormDirective, { static: true })
  gridRegistrationFormHost!: GridRegistrationFormDirective;

  privacyPolicyControl = new FormControl<boolean>(false);

  get pageHintAdaptionName() {
    return `gridRegistration.external.${this.model.gridRegistrationType}.pageHint`;
  }

  constructor(
    protected configService: ConfigService,
    public httpService: HttpHapService,
    public toastService: ToastrService,
    protected dataShareService: DataShareService,
    protected authorizationService: AuthorizationService,
    protected authenticationService: AuthenticationService,
    protected route: ActivatedRoute,
    public matDialog: MatDialog,
    public router: Router,
    protected gridRegistrationService: GridRegistrationRequestService,
    pageActionExecutor: PageActionExecutorService
  ) {
    super(
      configService,
      dataShareService,
      authorizationService,
      matDialog,
      httpService,
      toastService,
      pageActionExecutor
    );
  }

  insertMainForm(config: NBPConfig) {
    // put the form in :)
    const viewContainerRef = this.gridRegistrationFormHost.viewContainerRef;
    viewContainerRef.clear();

    // console.log(this.model, this.dataShareService.tenantConfig);
    const componentRef = viewContainerRef.createComponent(
      getComponentForGridRegistrationType(
        this.model.gridRegistrationType,
        this.model.formVersion || config.gridRegistration.gridRegistrationFormVersionEnum
      )
    );
    this.form = componentRef.instance;
    componentRef.instance.onErrorsChanged.subscribe((x) => this.onFormErrorsChanged(x));
  }

  ngOnInit(): void {
    // in case there is no existing gridRegistration we want to initialize it with the corresponding values
    this.model = new GridRegistrationModel();

    this.model.gridRegistrationType = GridRegistrationTypeEnum[this.route.snapshot.paramMap.get('gridType')];
    const displayName = GridRegistrationTypeConst.find(
      (option) => option.value === this.model.gridRegistrationType
    )?.displayName;
    this.title = `Netzanmeldung ${displayName}`.trim();

    // this.model.technician = this.user;

    if (this.model.gridRegistrationType === GridRegistrationTypeEnum.Charger) {
      this.model.consumers.push(new ConsumerModel('Charger'));
    }

    if (this.model.gridRegistrationType === GridRegistrationTypeEnum.EHeating) {
      this.model.consumers.push(new ConsumerModel('EHeating'));
    }

    if (this.model.gridRegistrationType === GridRegistrationTypeEnum.Storage) {
      this.model.consumers.push(new ConsumerModel('Storage'));
    }

    if (this.model.createdBy && this.model.createdBy === 'anonymousUser') {
      this.createdByCustomer = this.model.customer;
    }

    this.dataShareService.tenantConfig$
      .pipe(
        takeUntil(this.compUnsubs$),
        combineLatestWith(this.dataShareService.currentTenant$),
        // for some reaseon config emmits an empty object the first time
        filter(([config, tenant]) => !!config && !!config.gridRegistration && !!tenant),
        tap(([config, tenant]) => {
          // for the external process we seem to need the tenant?
          // check why this is the case. should be the same for internal/external usage
          this.model.tenant = tenant;
          this.insertMainForm(config);
          this.form.loadFormWithModel(this.model);
        })
      )
      .subscribe();
  }

  submit() {
    // Validate the form
    if (!this.form.isValid()) {
      console.log('form is not valid - ', this.form.getAllValidationErrors(), this.form);
      this.toastService.error(
        'Bitte überprüfen Sie Ihre Eingaben' +
          this.form
            .getAllValidationErrors()
            .map((errorModel) => ShowErrorModel.toReadableString(errorModel))
            .join('<br/>'),
        'Bitte überprüfen Sie Ihre Eingaben für folgende Felder:'
      );
      // log all the validation errors for all nested fields
      return;
    }
    if (!this.privacyPolicyControl.value) {
      this.toastService.error(
        'Bitte bestätigen Sie die Datenschutzbestimmungen',
        'Bitte bestätigen Sie die Datenschutzbestimmungen'
      );
      this.privacyPolicyControl.markAsTouched();
    }

    this.model = this.form.getModelFromForm();

    this.httpService
      .putOrPost<AnswerWithMoreInfoModel<GridRegistrationModel>>(
        { additionalData: this.model },
        uri.cmGridRegistrationRequestExternal
      )
      .pipe(
        takeUntil(this.compUnsub$),
        tap((answer) => {
          this.router.navigate([path.external + '/' + answer.navigateTo + '/' + answer.message], {
            replaceUrl: true
          });
        })
      )
      .subscribe({
        error: (error) => {
          if (error?.error?.message) {
            this.toastService.error(error.error.message);
          } else {
            this.toastService.error('Beim  Verschicken ist ein Fehler aufgetreten.');
          }
        }
      });
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
